// frontend/src/components/JobEditModal.jsx
// Project: Vision ANPR - Job Edit Modal (standalone)
// Author: Castle Traffic Ltd
// Updated: 2026-11-04 10:28 GMT (A24 manual completion lifecycle)
//
// Change Log:
//   2026-11-04 10:28 GMT: A24 dedicated completion action for Jobs without Work Packages; status/progress read-only, audited reason, confirmed transition and refresh.
//   2026-11-02 GMT: EXP-03 shared Job-prefilled Expense modal, job list and authenticated View receipt.
//   2026-10-28 13:44 GMT: Added standalone JobCostLedgerAccordion immediately after Expenses.
//   2026-10-20 10:45 BST: Added separate satellite Work Package Map Planner entry point.
//   2026-10-20 08:27 BST: Use each Work Package draft location rather than parent Job location.
// • 2026-10-07 15:42 BST: Added historic Job Work Package enablement/composition and workflow initiation.
//
// Notes:
// • Modal is portaled to <body> to avoid clipping under widgets.
// • High z-index on overlay/panel.
// • 15-minute date/time controls (00/15/30/45).
// • Job Ref duplicate check is evidence-only and now requires an *exact* match (trim + case-insensitive) excluding the current job.
// • Hardened location persistence with multiple POST/PUT/PATCH attempts and a verify GET.
// • Documents, Job Messages and Expenses accordions now share identical styling.
// • JobMessagesAccordian is mounted inside the Job Edit modal under “Job Messages”.
// • Flood Intelligence is rendered between Expected Weather and Authority using settled lat/lon only.
// • Traffic Intelligence card is rendered between Expected Weather and Flood Intelligence.
// • Works Intelligence card is rendered between Traffic Intelligence and Flood Intelligence using location-scoped Street Manager works data
//   so it matches the same settled lat/lon + time window context as the other live intelligence cards.
// • 2026-03-31 FIX: Week Commencing now persists via job meta save flow and no longer gets lost on Save.
// • 2026-03-31 FIX: When Week Commencing mode is enabled, assignment saves are skipped intentionally.
// • 2026-03-31 FIX: Save Changes now persists both job meta and assignment changes.
// • 2026-09-02 Q12: Added editable Opportunity + Quote commercial provenance selectors.
// • 2026-09-02 Q12 SAVE FIX: Job metadata save now uses canonical PATCH only and
//   sends only fields permitted by the hardened Job update endpoint.
// • 2026-09-02 Q-12: Added tenant-visible Opportunity and Quote provenance selectors.
// • Q-12 selectors are customer-scoped; Quote selection synchronises authoritative Opportunity provenance.
// • Q-12 persists existing jobs.opportunity_id and jobs.quote_id through the normal Job update flow.
// • 2026-09-30: Authority auto-resolution now displays the active ONS
//   LAD-to-county/unitary relationship returned by geo/admin-areas/by-point.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import ModalWrapper from "./ModalWrapper";
import LocationEditModal from "./LocationEditModal";
import QuoteCreateModal from "./QuoteCreateModal";
import JobMessagesAccordian from "./JobMessagesAccordian";
import WorkPackageComposer from "./WorkPackageComposer.jsx";
import WorkPackageMapPlanner from "./WorkPackageMapPlanner.jsx";
import JobCostLedgerAccordion from "./JobCostLedgerAccordion.jsx";

// NEW: Expected Weather card (job-scoped)
import ExpectedWeatherCard from "./ExpectedWeatherCard";

// NEW: Traffic intelligence card (job-scoped)
import TrafficIntelligenceCard from "./TrafficIntelligenceCard";

// NEW: Works intelligence card (location-aware / job-window aware)
import WorksIntelligenceCard from "./WorksIntelligenceCard";

// NEW: Flood intelligence card (job-scoped)
import FloodIntelligenceCard from "./FloodIntelligenceCard";

// NEW: channel modals
import EmailMessageModal from "./EmailMessageModal";
import SmsMessageModal from "./SmsMessageModal";
import WhatsAppMessageModal from "./WhatsAppMessageModal";

// ---------- tiny helpers ----------
const pad2 = (n) => String(n).padStart(2, "0");

const toLocalInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  try {
    const d = typeof value === "number" ? new Date(value) : new Date(String(value));
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const MM = pad2(d.getMonth() + 1);
    const DD = pad2(d.getDate());
    const hh = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    return `${yyyy}-${MM}-${DD}T${hh}:${mm}`;
  } catch {
    return "";
  }
};

const toDateOnlyInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  try {
    const d = typeof value === "number" ? new Date(value) : new Date(String(value));
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  } catch {
    return "";
  }
};

const isoNoMs = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
};

const extractHttpError = (e) => {
  const r = e?.response;
  if (!r) return e?.message || String(e);
  const d = r.data;
  const detail =
    (d && (typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail))) ||
    (typeof d === "string" ? d : JSON.stringify(d));
  return `${r.status} ${r.statusText || ""} - ${detail}`.trim();
};

const callJson = async (method, path, dataOrConfig) => {
  if (method === "get") return axiosInstance.get(path, dataOrConfig);
  if (method === "post") return axiosInstance.post(path, dataOrConfig);
  if (method === "put") return axiosInstance.put(path, dataOrConfig);
  if (method === "patch") return axiosInstance.patch(path, dataOrConfig);
  if (method === "delete") return axiosInstance.delete(path, dataOrConfig);
  throw new Error(`Unsupported method: ${method}`);
};

const pickLatestAssignment = (rows = []) =>
  rows.slice().sort((a, b) => new Date(b.starts_at || 0) - new Date(a.starts_at || 0))[0] || null;

const extractList = (payload, namedKey) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (namedKey && Array.isArray(payload?.[namedKey])) return payload[namedKey];
  return [];
};

const sameId = (a, b) => {
  if (a === null || a === undefined || a === "") return false;
  if (b === null || b === undefined || b === "") return false;
  return String(a) === String(b);
};

// ---------- 15-minute picker ----------
const HOUR_OPTS = Array.from({ length: 24 }, (_, h) => pad2(h));
const MINUTE_15 = ["00", "15", "30", "45"];

const splitLocal = (value) => {
  if (!value) return { date: "", hour: "", minute: "" };
  const s = toLocalInputValue(value);
  if (!s) return { date: "", hour: "", minute: "" };
  const [d, t] = s.split("T");
  const [hh, mm] = (t || "").split(":");
  return { date: d || "", hour: hh || "", minute: mm || "" };
};

const combineLocal = (date, hour, minute) => {
  if (!date || hour === "" || minute === "") return "";
  return `${date}T${hour}:${minute}`;
};

const DateTime15Picker = ({ label, value, onChange, disabled = false }) => {
  const { date, hour, minute } = splitLocal(value);
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <div className="grid grid-cols-[1fr_auto_auto] gap-2">
        <input
          type="date"
          className="w-full border rounded px-3 py-2 text-sm bg-white"
          value={date}
          disabled={disabled}
          onChange={(e) => onChange(combineLocal(e.target.value, hour || "00", minute || "00"))}
        />
        <select
          className="border rounded px-2 py-2 text-sm bg-white"
          value={hour || ""}
          disabled={disabled}
          onChange={(e) => onChange(combineLocal(date, e.target.value, minute || "00"))}
        >
          <option value="">hh</option>
          {HOUR_OPTS.map((h) => (
            <option key={`h-${h}`} value={h}>
              {h}
            </option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-2 text-sm bg-white"
          value={minute || ""}
          disabled={disabled}
          onChange={(e) => onChange(combineLocal(date, hour || "00", e.target.value))}
        >
          <option value="">mm</option>
          {MINUTE_15.map((m) => (
            <option key={`m-${m}`} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ---------- component ----------
const JobEditModal = ({ open, onClose, jobId, onSaved, maps = {} }) => {
  const {
    customerMap = {},
    contactMap = {},
    userMap = {},
    jobTypeMap = {},
    jobSubtypeMap = {},
    jobSubtype2Map = {},
    resourceMap = {},
  } = maps;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completionBusy, setCompletionBusy] = useState(false);
  const [completionReason, setCompletionReason] = useState("");
  const [completionError, setCompletionError] = useState("");
  const [workPackagesChecked, setWorkPackagesChecked] = useState(false);
  const [workPackagesLoadError, setWorkPackagesLoadError] = useState("");
  const [containsWorkPackages, setContainsWorkPackages] = useState(false);
  const [existingWorkPackages, setExistingWorkPackages] = useState([]);
  const [workPackageDrafts, setWorkPackageDrafts] = useState([]);
  const [workPackageSaving, setWorkPackageSaving] = useState(false);
  const [workPackageError, setWorkPackageError] = useState("");
  const [workPackageMapOpen, setWorkPackageMapOpen] = useState(false);

  // Q-12 commercial provenance selectors
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commercialError, setCommercialError] = useState("");

  useEffect(() => {
    if (!open || !jobId) return;
    let live = true;
    setWorkPackagesChecked(false);
    setWorkPackagesLoadError("");
    setExistingWorkPackages([]);
    setContainsWorkPackages(false);
    setCompletionError("");
    setCompletionReason("");
    axiosInstance.get(`/api/jobs/${jobId}/work-packages`).then(({ data }) => {
      if (!live) return;
      const rows = Array.isArray(data) ? data : [];
      setExistingWorkPackages(rows);
      setContainsWorkPackages(rows.length > 0);
      setWorkPackagesChecked(true);
    }).catch((error) => {
      if (!live) return;
      setWorkPackagesLoadError(extractHttpError(error));
      setWorkPackagesChecked(false); // fail closed: cannot establish that the Job has no WPs
    });
    return () => { live = false; };
  }, [open, jobId]);

  // --- form state ---
  const [form, setForm] = useState({
    job_ref: "",
    tenant_id: "",
    customer_id: "",
    contact_id: "",
    model_type: "",
    status: "",
    progress: "",
    current_step: "",
    task_id: "",
    notes: "",
    processed_by: "",
    job_type_id: "",
    job_subtype_id: "",
    job_subtype2_id: "",
    opportunity_id: "",
    quote_id: "",
    scheduled_start: "",
    scheduled_end: "",
    assigned_resource_id: "",
    co_assigned_resource_id: "",
    location_id: "",
    week_commencing: "",
    use_week_commencing: false,
  });

  // Job ref duplicate check
  const [refCheckLoading, setRefCheckLoading] = useState(false);
  const [refDup, setRefDup] = useState(false);
  const [refEvidence, setRefEvidence] = useState([]);

  // initial refs/meta
  const initialAssignedRef = useRef(null);
  const initialCoRef = useRef(null);
  const initialStartRef = useRef("");
  const initialEndRef = useRef("");
  const initialRefRef = useRef("");
  const initialMetaRef = useRef(null);

  // meta
  const [locationName, setLocationName] = useState("");
  const [locationCoords, setLocationCoords] = useState({ lat: null, lon: null });
  const [locPickerOpen, setLocPickerOpen] = useState(false);
  const [locCreateOpen, setLocCreateOpen] = useState(false);
  const [locEditOpen, setLocEditOpen] = useState(false);
  const [showLinkedQuote, setShowLinkedQuote] = useState(false);
  const [persistedQuoteId, setPersistedQuoteId] = useState(null);
  const [locationLoadingForEdit, setLocationLoadingForEdit] = useState(false);
  const [locationEditError, setLocationEditError] = useState("");

  // Authority auto-resolve (by point -> directory)
  const [adminArea, setAdminArea] = useState(null);
  const [authorityEntry, setAuthorityEntry] = useState(null);
  const [authorityLoading, setAuthorityLoading] = useState(false);
  const [authorityErr, setAuthorityErr] = useState("");

  // inline ref save
  const [savingRef, setSavingRef] = useState(false);
  const [refMsg, setRefMsg] = useState("");
  const [refErr, setRefErr] = useState("");

  // docs
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docPortalVisible, setDocPortalVisible] = useState(false);
  const [docTags, setDocTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(false);
  const [savingDocFlags, setSavingDocFlags] = useState(false);

  // expenses
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenseErr, setExpenseErr] = useState("");
  const [tabDocsOpen, setTabDocsOpen] = useState(false);
  const [tabExpensesOpen, setTabExpensesOpen] = useState(false);

  // availability
  const [availability, setAvailability] = useState({});

  // Job Expenses: creation is delegated to the shared EXP-03 modal.
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptBusyId, setReceiptBusyId] = useState(null);
  const receiptUrlRef = useRef(null);
  const receiptRequestRef = useRef(null);

  // channel modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);

  const [activeMessageContext, setActiveMessageContext] = useState(null);

  const onChange = (field, val) =>
    setForm((f) => ({
      ...f,
      [field]: val,
    }));

  const handleCustomerChange = (value) => {
    const nextCustomerId = value === null || value === undefined ? "" : String(value);
    setForm((f) => {
      const selectedOpportunity = opportunities.find((o) => sameId(o?.id, f.opportunity_id));
      const selectedQuote = quotes.find((q) => sameId(q?.id, f.quote_id));

      const keepOpportunity =
        selectedOpportunity && sameId(selectedOpportunity?.customer_id ?? selectedOpportunity?.customerId, nextCustomerId);
      const keepQuote = selectedQuote && sameId(selectedQuote?.customer_id ?? selectedQuote?.customerId, nextCustomerId);

      return {
        ...f,
        customer_id: nextCustomerId,
        opportunity_id: keepOpportunity ? f.opportunity_id : "",
        quote_id: keepQuote ? f.quote_id : "",
      };
    });
  };

  const handleOpportunityChange = (value) => {
    const nextOpportunityId = value === null || value === undefined ? "" : String(value);

    setForm((f) => {
      if (!nextOpportunityId) {
        const selectedQuote = quotes.find((q) => sameId(q?.id, f.quote_id));
        const quoteOpportunityId =
          selectedQuote?.opportunity_id ?? selectedQuote?.opportunityId ?? null;

        return {
          ...f,
          opportunity_id: "",
          quote_id: quoteOpportunityId ? "" : f.quote_id,
        };
      }

      const selectedQuote = quotes.find((q) => sameId(q?.id, f.quote_id));
      const quoteOpportunityId =
        selectedQuote?.opportunity_id ?? selectedQuote?.opportunityId ?? null;

      return {
        ...f,
        opportunity_id: nextOpportunityId,
        quote_id:
          f.quote_id && quoteOpportunityId && !sameId(quoteOpportunityId, nextOpportunityId)
            ? ""
            : f.quote_id,
      };
    });
  };

  const handleQuoteChange = (value) => {
    const nextQuoteId = value === null || value === undefined ? "" : String(value);

    setForm((f) => {
      if (!nextQuoteId) {
        return { ...f, quote_id: "" };
      }

      const selectedQuote = quotes.find((q) => sameId(q?.id, nextQuoteId));
      if (!selectedQuote) {
        return { ...f, quote_id: nextQuoteId };
      }

      const quoteCustomerId = selectedQuote?.customer_id ?? selectedQuote?.customerId ?? null;
      const quoteOpportunityId = selectedQuote?.opportunity_id ?? selectedQuote?.opportunityId ?? null;

      return {
        ...f,
        customer_id: quoteCustomerId ? String(quoteCustomerId) : f.customer_id,
        opportunity_id: quoteOpportunityId ? String(quoteOpportunityId) : "",
        quote_id: nextQuoteId,
      };
    });
  };

  // Only compare fields that the canonical Job update endpoint permits.
  // Workflow/runtime fields such as status/progress/current_step/task_id are
  // intentionally governed elsewhere and must not be sent through PATCH /jobs/{id}.
  const normaliseMetaForCompare = (f) => ({
    customer_id: f.customer_id ? String(f.customer_id) : "",
    contact_id: f.contact_id ? String(f.contact_id) : "",
    model_type: String(f.model_type || ""),
    job_type_id: f.job_type_id ? String(f.job_type_id) : "",
    job_subtype_id: f.job_subtype_id ? String(f.job_subtype_id) : "",
    job_subtype2_id: f.job_subtype2_id ? String(f.job_subtype2_id) : "",
    opportunity_id: f.opportunity_id ? String(f.opportunity_id) : "",
    quote_id: f.quote_id ? String(f.quote_id) : "",
    week_commencing: String(f.week_commencing || ""),
  });

  const resolveAuthorityForPoint = async (lat, lng) => {
    setAuthorityErr("");
    setAdminArea(null);
    setAuthorityEntry(null);

    if (lat == null || lng == null) return;

    setAuthorityLoading(true);
    try {
      const res = await callJson("get", "geo/admin-areas/by-point", {
        params: { lat, lng },
      });
      const area = res?.data || null;

      if (!area || !area.gss_code) {
        setAdminArea(null);
        setAuthorityEntry(null);
        return;
      }

      setAdminArea(area);

      const r2 = await callJson("get", "authority-directory", {
        params: { q: area.gss_code, limit: 1 },
      });
      const arr = Array.isArray(r2?.data) ? r2.data : Array.isArray(r2?.data?.items) ? r2.data.items : [];
      const entry = arr.length ? arr[0] : null;

      setAuthorityEntry(entry);
    } catch (e) {
      setAuthorityErr(extractHttpError(e));
      setAdminArea(null);
      setAuthorityEntry(null);
    } finally {
      setAuthorityLoading(false);
    }
  };

  // ---------- Q-12 commercial provenance catalogue ----------
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadCommercialOptions = async () => {
      setCommercialLoading(true);
      setCommercialError("");

      try {
        const [opportunityResult, quoteResult] = await Promise.allSettled([
          callJson("get", "opportunities", { params: { limit: 500, offset: 0 } }),
          callJson("get", "quotes", { params: { limit: 1000, offset: 0, sort: "updated_at:desc" } }),
        ]);

        if (cancelled) return;

        if (opportunityResult.status === "fulfilled") {
          setOpportunities(extractList(opportunityResult.value?.data, "opportunities"));
        } else {
          setOpportunities([]);
        }

        if (quoteResult.status === "fulfilled") {
          setQuotes(extractList(quoteResult.value?.data, "quotes"));
        } else {
          setQuotes([]);
        }

        const errors = [];
        if (opportunityResult.status === "rejected") {
          errors.push(`Opportunities: ${extractHttpError(opportunityResult.reason)}`);
        }
        if (quoteResult.status === "rejected") {
          errors.push(`Quotes: ${extractHttpError(quoteResult.reason)}`);
        }
        setCommercialError(errors.join(" | "));
      } finally {
        if (!cancelled) setCommercialLoading(false);
      }
    };

    loadCommercialOptions();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // ---------- load job + assignments ----------
  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const jRes = await callJson("get", `jobs/${jobId}`);
        if (cancelled) return;
        const j = jRes.data || {};
        setPersistedQuoteId(j.quote_id ?? null);

        let rows = [];
        try {
          const r2 = await callJson("get", `jobs/${jobId}/resources`);
          rows = Array.isArray(r2.data) ? r2.data : [];
        } catch {
          rows = [];
        }

        const latest = pickLatestAssignment(rows);
        const schedStart = latest?.starts_at || "";
        const schedEnd = latest?.ends_at || "";
        const rid = latest?.resource_id != null ? String(latest.resource_id) : "";

        let coRid = "";
        if (latest) {
          const match = rows.find(
            (a) =>
              a.id !== latest.id &&
              a.starts_at === latest.starts_at &&
              a.ends_at === latest.ends_at
          );
          if (match?.resource_id != null) coRid = String(match.resource_id);
        }

        const nextForm = {
          job_ref: j.job_ref ?? "",
          tenant_id: j.tenant_id ?? "",
          customer_id: j.customer_id ?? "",
          contact_id: j.contact_id ?? "",
          model_type: j.model_type ?? "",
          status: j.status ?? "",
          progress: j.progress ?? "",
          current_step: j.current_step ?? "",
          task_id: j.task_id ?? "",
          notes: j.notes ?? "",
          processed_by: j.processed_by ?? "",
          job_type_id: j.job_type_id ?? "",
          job_subtype_id: j.job_subtype_id ?? "",
          job_subtype2_id: j.job_subtype2_id ?? "",
          opportunity_id: j.opportunity_id ?? "",
          quote_id: j.quote_id ?? "",
          scheduled_start: schedStart,
          scheduled_end: schedEnd,
          assigned_resource_id: rid,
          co_assigned_resource_id: coRid,
          location_id: j.location_id != null ? String(j.location_id) : "",
          week_commencing: j.week_commencing ? toDateOnlyInputValue(j.week_commencing) : "",
          use_week_commencing: !!j.use_week_commencing || !!j.week_commencing,
        };

        setForm((prev) => ({ ...prev, ...nextForm }));

        initialAssignedRef.current = rid || null;
        initialCoRef.current = coRid || null;
        initialStartRef.current = schedStart || "";
        initialEndRef.current = schedEnd || "";
        initialRefRef.current = j.job_ref ?? "";
        initialMetaRef.current = normaliseMetaForCompare(nextForm);

        if (j.location_id) {
          try {
            const lr = await callJson("get", `locations/${j.location_id}`);
            const loc = lr.data || {};
            setLocationName(loc.name || `Location #${j.location_id}`);
            setLocationCoords({
              lat: loc.lat ?? null,
              lon: loc.lon ?? null,
            });
          } catch {
            setLocationName(j.location_id ? `Location #${j.location_id}` : "");
            setLocationCoords({ lat: null, lon: null });
          }
        } else {
          setLocationName("");
          setLocationCoords({ lat: null, lon: null });
        }

        const loadRecentDocs = async () => {
          setLoadingDocs(true);
          try {
            const r1 = await callJson("get", `jobs/${jobId}/documents`);
            const arr = Array.isArray(r1.data) ? r1.data : [];
            const rowsOut = arr
              .map((d) => ({
                id: d.id,
                name: d.name || `Document #${d.id}`,
                latest_url: d.latest_url || d.url || "",
                latest_version: d.latest_version || 1,
                size_bytes: d.size_bytes ?? d.latest_size_bytes ?? null,
                uploaded_at: d.updated_at || d.created_at || "",
                portal_visible: !!d.portal_visible,
                downloadable: !!d.downloadable,
                mime_type: d.mime_type || "",
                document_key: d.document_key || "",
              }))
              .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
            setDocs(rowsOut);
          } finally {
            setLoadingDocs(false);
          }
        };

        const loadExpenses = async () => {
          setLoadingExpenses(true);
          setExpenseErr("");
          try {
            try {
              const r = await callJson("get", `jobs/${jobId}/expenses`);
              setExpenses(extractList(r.data, "expenses"));
              return;
            } catch {
              const r2 = await callJson("get", "expenses", {
                params: { job_id: jobId },
              });
              setExpenses(extractList(r2.data, "expenses").filter((row) => String(row.job_id) === String(jobId)));
              return;
            }
          } catch (e) {
            setExpenses([]);
            setExpenseErr(extractHttpError(e));
          } finally {
            setLoadingExpenses(false);
          }
        };

        await Promise.all([loadRecentDocs(), loadExpenses()]);
      } catch (e) {
        console.error("Failed to load job:", e?.response?.data || e?.message || e);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    const refresh = async (event) => {
      if (event?.detail?.job_id && String(event.detail.job_id) !== String(jobId)) return;
      setLoadingExpenses(true);
      try {
        const response = await callJson("get", "expenses", { params: { job_id: jobId } });
        const rows = extractList(response.data, "expenses");
        setExpenses(rows.filter((row) => String(row.job_id) === String(jobId)));
        setExpenseErr("");
      } catch (err) { setExpenseErr(extractHttpError(err)); }
      finally { setLoadingExpenses(false); }
    };
    window.addEventListener("vision:expense-created", refresh);
    return () => window.removeEventListener("vision:expense-created", refresh);
  }, [open, jobId]);

  useEffect(() => () => {
    receiptRequestRef.current?.abort();
    if (receiptUrlRef.current) URL.revokeObjectURL(receiptUrlRef.current);
  }, []);

  const closeExpenseReceipt = () => {
    receiptRequestRef.current?.abort();
    receiptRequestRef.current = null;
    if (receiptUrlRef.current) URL.revokeObjectURL(receiptUrlRef.current);
    receiptUrlRef.current = null;
    setReceiptPreview(null);
    setReceiptBusyId(null);
  };
  const viewExpenseReceipt = async (expense) => {
    if (!expense?.id || !expense?.receipt_url) return;
    closeExpenseReceipt();
    if (String(expense.receipt_url) !== `/api/expenses/${expense.id}/receipt/view`) {
      const legacy = String(expense.receipt_url);
      if (/^(https?:\/\/|\/)/i.test(legacy)) window.open(legacy, "_blank", "noopener,noreferrer");
      return;
    }
    const controller = new AbortController();
    receiptRequestRef.current = controller;
    setReceiptBusyId(expense.id);
    try {
      const response = await axiosInstance.get(`expenses/${expense.id}/receipt/view`, {
        responseType: "blob", signal: controller.signal, timeout: 30000,
      });
      if (controller.signal.aborted) return;
      const mime = (response.data.type || response.headers?.["content-type"] || "").split(";")[0].toLowerCase();
      if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(mime)) throw new Error("Unsupported receipt format");
      const url = URL.createObjectURL(response.data);
      receiptUrlRef.current = url;
      setReceiptPreview({ url, mime, expenseId: expense.id });
    } catch (err) {
      if (!controller.signal.aborted) setExpenseErr(extractHttpError(err));
    } finally {
      if (receiptRequestRef.current === controller) {
        receiptRequestRef.current = null;
        setReceiptBusyId(null);
      }
    }
  };

  // ---------- authority auto-lookup ----------
  useEffect(() => {
    if (!open) return;

    const lat = locationCoords?.lat;
    const lon = locationCoords?.lon;

    if (lat == null || lon == null || lat === "" || lon === "") {
      setAuthorityErr("");
      setAdminArea(null);
      setAuthorityEntry(null);
      return;
    }

    const t = setTimeout(() => {
      resolveAuthorityForPoint(Number(lat), Number(lon));
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationCoords?.lat, locationCoords?.lon]);

  // ---------- availability lookup ----------
  useEffect(() => {
    if (!open || form.use_week_commencing) return;
    const timer = setTimeout(async () => {
      try {
        const from = form.scheduled_start ? isoNoMs(form.scheduled_start) : null;
        const to = form.scheduled_end ? isoNoMs(form.scheduled_end) : null;
        if (!from || !to) {
          const m = {};
          Object.keys(resourceMap).forEach((id) => (m[Number(id)] = "unknown"));
          setAvailability(m);
          return;
        }
        try {
          const res = await callJson("get", "resources/availability", {
            params: { from, to, limit: 5000 },
          });
          const arr = Array.isArray(res.data) ? res.data : [];
          const m = {};
          Object.keys(resourceMap).forEach((id) => (m[Number(id)] = "unknown"));
          arr.forEach((row) => {
            if (row?.resource_id == null) return;
            m[row.resource_id] = row.available ? "available" : "busy";
          });
          setAvailability(m);
        } catch {
          const m = {};
          Object.keys(resourceMap).forEach((id) => (m[Number(id)] = "unknown"));
          setAvailability(m);
        }
      } catch {
        /* ignore */
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [open, form.scheduled_start, form.scheduled_end, form.use_week_commencing, resourceMap]);

  // ---------- live duplicate check ----------
  useEffect(() => {
    if (!open) return;
    const trimmed = String(form.job_ref || "").trim();
    if (!trimmed || trimmed === initialRefRef.current) {
      setRefDup(false);
      setRefEvidence([]);
      return;
    }

    let cancelled = false;
    setRefCheckLoading(true);

    const fetchCandidates = async () => {
      const attempts = [
        ["jobs", { job_ref: trimmed, limit: 50 }],
        ["jobs", { q: trimmed, limit: 50 }],
        ["jobs/search", { q: trimmed, limit: 50 }],
        [`jobs/ref/${encodeURIComponent(trimmed)}`, null],
      ];

      const collected = [];
      for (const [path, params] of attempts) {
        try {
          const resp = await axiosInstance.get(path, params ? { params } : undefined);
          const data = resp?.data;

          const arr = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
            ? data.results
            : data?.id || data?.job_ref
            ? [data]
            : [];

          for (const j of arr) {
            if (j && j.id != null) collected.push(j);
          }
        } catch {
          // ignore
        }
      }
      if (collected.length === 0) {
        try {
          const r = await axiosInstance.get("jobs", { params: { limit: 100 } });
          const data = r?.data;
          const arr = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
          arr.forEach((j) => j && j.id != null && collected.push(j));
        } catch {
          // ignore
        }
      }

      const map = new Map();
      collected.forEach((j) => map.set(Number(j.id), j));
      return Array.from(map.values());
    };

    const t = setTimeout(async () => {
      try {
        const candidates = await fetchCandidates();

        const eq = (a, b) =>
          String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
        const matches = candidates.filter(
          (j) => eq(j.job_ref, trimmed) && Number(j.id) !== Number(jobId)
        );

        setRefDup(matches.length > 0);
        setRefEvidence(matches.slice(0, 3).map((j) => ({ id: j.id, job_ref: j.job_ref })));
      } catch {
        setRefDup(false);
        setRefEvidence([]);
      } finally {
        if (!cancelled) setRefCheckLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.job_ref, open, jobId]);

  // ---------- actions ----------
  const saveJobMetaIfChanged = async () => {
    if (form.use_week_commencing && form.week_commencing) {
      const selectedWeek = new Date(`${form.week_commencing}T12:00:00`);
      if (Number.isNaN(selectedWeek.getTime()) || selectedWeek.getDay() !== 1) {
        throw new Error("Week Commencing must be a Monday.");
      }
    }

    const current = normaliseMetaForCompare(form);
    const initial = initialMetaRef.current || {};

    if (JSON.stringify(current) === JSON.stringify(initial)) {
      return { changed: false, message: "No changes to job details." };
    }

    // Canonical PATCH /jobs/{id} accepts controlled Job metadata only.
    // Do not send workflow/runtime-owned fields (status, progress, current_step,
    // task_id, processed_by, etc.) because the backend correctly rejects them.
    const payload = {
      customer_id: form.customer_id ? Number(form.customer_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      model_type: form.model_type || null,
      job_type_id: form.job_type_id ? Number(form.job_type_id) : null,
      job_subtype_id: form.job_subtype_id ? Number(form.job_subtype_id) : null,
      job_subtype2_id: form.job_subtype2_id ? Number(form.job_subtype2_id) : null,
      opportunity_id: form.opportunity_id ? Number(form.opportunity_id) : null,
      quote_id: form.quote_id ? Number(form.quote_id) : null,
      week_commencing: form.use_week_commencing ? (form.week_commencing || null) : null,
    };

    // PATCH is the registered canonical update route. Do not mask a useful 4xx
    // validation response by falling through to the nonexistent /update route.
    const res = await callJson("patch", `jobs/${jobId}`, payload);
    const updated = res?.data || null;

    if (!updated) {
      throw new Error("Job update returned no data.");
    }

    const authoritativeMeta = {
      ...form,
      customer_id: updated?.customer_id ?? form.customer_id,
      opportunity_id: updated?.opportunity_id ?? "",
      quote_id: updated?.quote_id ?? "",
      week_commencing: updated?.week_commencing ? toDateOnlyInputValue(updated.week_commencing) : "",
      use_week_commencing: !!updated?.use_week_commencing || !!updated?.week_commencing,
    };

    setForm((f) => ({ ...f, ...authoritativeMeta }));
    initialMetaRef.current = normaliseMetaForCompare(authoritativeMeta);
    setPersistedQuoteId(updated?.quote_id ?? null);

    return { changed: true, message: "Job details saved.", updated };
  };

  const saveAssignmentIfChanged = async () => {
    if (form.use_week_commencing) {
      return { changed: false, message: "Week commencing mode enabled; exact assignment save skipped." };
    }

    const newRid = form.assigned_resource_id ? Number(form.assigned_resource_id) : null;
    const oldRid = initialAssignedRef.current != null ? Number(initialAssignedRef.current) : null;

    const timesChanged =
      String(initialStartRef.current || "") !== String(form.scheduled_start || "") ||
      String(initialEndRef.current || "") !== String(form.scheduled_end || "");

    if (newRid === oldRid && !timesChanged) {
      return { changed: false, message: "No changes to primary assignment." };
    }

    if (!form.scheduled_start || !form.scheduled_end) {
      throw new Error("Assignment requires Scheduled Start and End. Set both times, then try again.");
    }
    if (!newRid) throw new Error("Assigned Resource is required");

    const starts_at = isoNoMs(form.scheduled_start);
    const ends_at = isoNoMs(form.scheduled_end);

    await callJson("post", `jobs/${jobId}/resources`, {
      resource_id: newRid,
      starts_at,
      ends_at,
      status: "assigned",
    });

    initialAssignedRef.current = String(newRid);
    initialStartRef.current = form.scheduled_start;
    initialEndRef.current = form.scheduled_end;

    return { changed: true, message: "Primary assignment saved." };
  };

  const saveCoAssignmentIfSelected = async () => {
    if (form.use_week_commencing) {
      return { changed: false, message: "Week commencing mode enabled; co-resource save skipped." };
    }

    const co = form.co_assigned_resource_id ? Number(form.co_assigned_resource_id) : null;
    if (!co) return { changed: false, message: "No co-resource selected." };
    if (String(co) === String(form.assigned_resource_id || "")) {
      return { changed: false, message: "Co-resource equals primary; skipped." };
    }
    if (!form.scheduled_start || !form.scheduled_end) {
      throw new Error("Co-assignment also requires Scheduled Start and End. Set both times, then try again.");
    }

    const starts_at = isoNoMs(form.scheduled_start);
    const ends_at = isoNoMs(form.scheduled_end);

    const initialCo = initialCoRef.current != null ? Number(initialCoRef.current) : null;
    const timesChanged =
      String(initialStartRef.current || "") !== String(form.scheduled_start || "") ||
      String(initialEndRef.current || "") !== String(form.scheduled_end || "");
    if (initialCo === co && !timesChanged) {
      return { changed: false, message: "No changes to co-assignment." };
    }

    await callJson("post", `jobs/${jobId}/resources`, {
      resource_id: co,
      starts_at,
      ends_at,
      status: "assigned",
    });

    initialCoRef.current = String(co);
    return { changed: true, message: "Co-resource assignment saved." };
  };

  const saveJobRef = async () => {
    if (!jobId) return;
    const trimmed = String(form.job_ref || "").trim();
    if (trimmed === initialRefRef.current) {
      setRefMsg("No changes to Job Ref.");
      setRefErr("");
      return;
    }
    if (refDup) {
      setRefErr("Duplicate Job Ref detected. Choose a unique reference.");
      setRefMsg("");
      return;
    }
    setSavingRef(true);
    setRefErr("");
    setRefMsg("");
    try {
      await callJson("post", `jobs/${jobId}/ref`, { job_ref: trimmed });
      initialRefRef.current = trimmed;
      setRefMsg("Job Ref updated.");
      onSaved?.({ ok: true, message: "Job Ref updated." });
    } catch (e) {
      setRefErr(extractHttpError(e));
      onSaved?.({ ok: false, message: extractHttpError(e) });
    } finally {
      setSavingRef(false);
    }
  };

  // A24: Manual completion is separate from the metadata PATCH.
  // The backend checks permissions, tenant, current status and Work Packages.
  const completeJobWithoutWorkPackages = async () => {
    if (!jobId || completionBusy || !workPackagesChecked || existingWorkPackages.length || containsWorkPackages || workPackageDrafts.length) return;
    if (String(form.status).toLowerCase() === "completed") return;
    const reason = completionReason.trim();
    if (reason.length < 8) {
      setCompletionError("Enter a completion reason of at least 8 characters.");
      return;
    }
    if (!window.confirm(`Mark Job ${form.job_ref || jobId} complete at 100%?\n\nThis will close the Job operationally.`)) return;
    setCompletionBusy(true);
    setCompletionError("");
    try {
      const response = await axiosInstance.post(`jobs/${jobId}/complete`, { reason });
      const updated = response?.data?.job;
      if (!updated || String(updated.status).toLowerCase() !== "completed" || Number(updated.progress) !== 100) {
        throw new Error("Completion endpoint did not confirm persisted completed / 100% status.");
      }
      setForm((current) => ({ ...current, status: updated.status, progress: updated.progress }));
      setCompletionReason("");
      onSaved?.({ ok: true, message: `Job ${form.job_ref || jobId} completed (100%).` });
    } catch (error) {
      setCompletionError(extractHttpError(error));
    } finally {
      setCompletionBusy(false);
    }
  };

  const saveJob = async () => {
    if (!jobId) return;
    setSaving(true);
    try {
      const meta = await saveJobMetaIfChanged();
      const primary = await saveAssignmentIfChanged();
      const co = await saveCoAssignmentIfSelected();

      const anyChanged = !!(meta?.changed || primary?.changed || co?.changed);
      const msg =
        co?.changed
          ? co.message
          : primary?.changed
          ? primary.message
          : meta?.changed
          ? meta.message
          : "No changes to save.";

      onSaved?.({ ok: true, message: msg, changed: anyChanged });
      onClose?.();
    } catch (e) {
      const msg = extractHttpError(e) || e?.message || String(e);
      onSaved?.({ ok: false, message: msg });
    } finally {
      setSaving(false);
    }
  };

  const mapToOptions = (m, unknownPrefix) => {
    const opts = Object.keys(m)
      .sort((a, b) => String(m[a]).localeCompare(String(m[b])))
      .map((id) => ({ value: String(id), label: String(m[id]) }));
    const ensureValueInOptions = (value) => {
      if (value === "" || value === null || value === undefined) return opts;
      const v = String(value);
      if (!opts.some((o) => o.value === v)) {
        opts.unshift({ value: v, label: `${unknownPrefix || "Unknown"}` });
      }
      return opts;
    };
    return { opts, ensureValueInOptions };
  };

  const renderSelect = (field, label, m, placeholder = "Select…", unknownPrefix = label) => {
    const { opts, ensureValueInOptions } = mapToOptions(m, unknownPrefix);
    const value = form[field] === null || form[field] === undefined ? "" : String(form[field]);
    const finalOpts = ensureValueInOptions(value);

    return (
      <div>
        <label className="block text-xs text-gray-600 mb-1">{label}</label>
        <select
          className="w-full border rounded px-3 py-2 text-sm bg-white"
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
        >
          <option value="">{placeholder}</option>
          {finalOpts.map((o) => (
            <option key={`${field}-${o.value}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const customerScopedOpportunities = useMemo(() => {
    if (!form.customer_id) return [];
    return opportunities
      .filter((o) => sameId(o?.customer_id ?? o?.customerId, form.customer_id))
      .slice()
      .sort((a, b) => {
        const aLabel = String(a?.title ?? a?.name ?? a?.opportunity_name ?? `Opportunity ${a?.id ?? ""}`);
        const bLabel = String(b?.title ?? b?.name ?? b?.opportunity_name ?? `Opportunity ${b?.id ?? ""}`);
        return aLabel.localeCompare(bLabel);
      });
  }, [opportunities, form.customer_id]);

  const customerScopedQuotes = useMemo(() => {
    if (!form.customer_id) return [];

    return quotes
      .filter((q) => sameId(q?.customer_id ?? q?.customerId, form.customer_id))
      .filter((q) => {
        if (!form.opportunity_id) return true;
        const qOpportunityId = q?.opportunity_id ?? q?.opportunityId ?? null;
        return sameId(qOpportunityId, form.opportunity_id);
      })
      .slice()
      .sort((a, b) => {
        const aTime = Date.parse(a?.updated_at ?? a?.updatedAt ?? a?.created_at ?? a?.createdAt ?? "") || Number(a?.id) || 0;
        const bTime = Date.parse(b?.updated_at ?? b?.updatedAt ?? b?.created_at ?? b?.createdAt ?? "") || Number(b?.id) || 0;
        return bTime - aTime;
      });
  }, [quotes, form.customer_id, form.opportunity_id]);

  const renderCommercialSelectors = () => {
    const selectedOpportunityId = form.opportunity_id ? String(form.opportunity_id) : "";
    const selectedQuoteId = form.quote_id ? String(form.quote_id) : "";

    const selectedOpportunityKnown = customerScopedOpportunities.some((o) => sameId(o?.id, selectedOpportunityId));
    const selectedQuoteKnown = customerScopedQuotes.some((q) => sameId(q?.id, selectedQuoteId));

    return (
      <>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Opportunity</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm bg-white"
            value={selectedOpportunityId}
            disabled={!form.customer_id || commercialLoading}
            onChange={(e) => handleOpportunityChange(e.target.value)}
          >
            <option value="">
              {!form.customer_id
                ? "Select a customer first…"
                : commercialLoading
                ? "Loading opportunities…"
                : "No Opportunity"}
            </option>
            {selectedOpportunityId && !selectedOpportunityKnown && (
              <option value={selectedOpportunityId}>Opportunity #{selectedOpportunityId} — currently linked</option>
            )}
            {customerScopedOpportunities.map((o) => {
              const id = String(o?.id ?? "");
              const title = String(o?.title ?? o?.name ?? o?.opportunity_name ?? "").trim();
              return (
                <option key={`opportunity-${id}`} value={id}>
                  {title ? `${title} — Opportunity #${id}` : `Opportunity #${id}`}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Quote</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm bg-white"
            value={selectedQuoteId}
            disabled={!form.customer_id || commercialLoading}
            onChange={(e) => handleQuoteChange(e.target.value)}
          >
            <option value="">
              {!form.customer_id
                ? "Select a customer first…"
                : commercialLoading
                ? "Loading quotes…"
                : "No Quote"}
            </option>
            {selectedQuoteId && !selectedQuoteKnown && (
              <option value={selectedQuoteId}>Quote #{selectedQuoteId} — currently linked</option>
            )}
            {customerScopedQuotes.map((q) => {
              const id = String(q?.id ?? "");
              const title = String(q?.title ?? q?.quote_ref ?? q?.quoteRef ?? "").trim();
              const status = String(q?.status ?? "").trim();
              const parts = [title || `Quote #${id}`, `Quote #${id}`];
              if (status) parts.push(status.charAt(0).toUpperCase() + status.slice(1));
              return (
                <option key={`quote-${id}`} value={id}>
                  {parts.join(" — ")}
                </option>
              );
            })}
          </select>
          <button
            type="button"
            className={`mt-2 min-h-10 rounded px-4 py-2 text-sm font-semibold text-white ${persistedQuoteId ? "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick" : "bg-gray-400 cursor-not-allowed"}`}
            disabled={!persistedQuoteId}
            title={persistedQuoteId ? `Open linked Quote #${persistedQuoteId}` : "This Job has no saved linked Quote"}
            onClick={() => setShowLinkedQuote(true)}
          >
            Show Quote
          </button>
          {selectedQuoteId && !sameId(selectedQuoteId, persistedQuoteId) && (
            <div className="mt-1 text-xs text-amber-700">Save Changes to persist the selected Quote before opening it.</div>
          )}
          {form.opportunity_id && form.customer_id && !commercialLoading && (
            <div className="text-[11px] text-gray-500 mt-1">
              Quotes are filtered to the selected customer and Opportunity.
            </div>
          )}
        </div>

        {commercialError && (
          <div className="md:col-span-2 text-[11px] text-red-700">
            Could not fully load commercial links: {commercialError}
          </div>
        )}
      </>
    );
  };

  // ---------- documents ----------
  const acceptTypes = [
    ".doc,.docx,.xls,.xlsx,.ppt,.pptx",
    ".pdf",
    "image/*",
    "video/*",
  ].join(",");

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    if (!bytes || isNaN(bytes)) return "-";
    const k = 1024;
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
  };

  const formatDateGB = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const handleFilesPicked = async (evt) => {
    const picked = Array.from(evt.target.files || []);
    if (!picked.length) return;

    const tenant_id = Number(form.tenant_id || 0);
    const customer_id = Number(form.customer_id || 0);
    const job_ref = form.job_ref || "";
    if (!tenant_id || !customer_id) {
      setUploadErr("Missing tenant/customer on this job.");
      evt.target.value = "";
      return;
    }

    setUploadErr("");
    setUploading(true);
    try {
      const tryPerJob = async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        if (job_ref) fd.append("job_ref", job_ref);
        if (docTags.trim()) fd.append("tags", docTags);
        fd.append("portal_visible", String(!!docPortalVisible));
        await callJson("post", `jobs/${jobId}/documents/upload`, fd);
      };

      for (const f of picked) {
        await tryPerJob(f);
      }

      setLoadingDocs(true);
      try {
        const r1 = await callJson("get", `jobs/${jobId}/documents`);
        const arr = Array.isArray(r1.data) ? r1.data : [];
        const rows = arr
          .map((d) => ({
            id: d.id,
            name: d.name || `Document #${d.id}`,
            latest_url: d.latest_url || d.url || "",
            latest_version: d.latest_version || 1,
            size_bytes: d.size_bytes ?? d.latest_size_bytes ?? null,
            uploaded_at: d.updated_at || d.created_at || "",
            portal_visible: !!d.portal_visible,
            downloadable: !!d.downloadable,
            mime_type: d.mime_type || "",
            document_key: d.document_key || "",
          }))
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
        setDocs(rows);
      } finally {
        setLoadingDocs(false);
      }
    } catch (e) {
      const msg = extractHttpError(e);
      setUploadErr(msg);
    } finally {
      setUploading(false);
      evt.target.value = "";
    }
  };

  const toggleDocPortal = (id) => {
    setDocs((rows) => rows.map((r) => (r.id === id ? { ...r, portal_visible: !r.portal_visible } : r)));
  };

  const toggleDocDownloadable = (id) => {
    setDocs((rows) => rows.map((r) => (r.id === id ? { ...r, downloadable: !r.downloadable } : r)));
  };

  const saveDocFlags = async () => {
    if (!jobId) return;
    setSavingDocFlags(true);
    try {
      for (const d of docs) {
        await callJson("patch", `documents/${d.id}`, {
          portal_visible: !!d.portal_visible,
          downloadable: !!d.downloadable,
        });
      }
    } catch (e) {
      const msg = extractHttpError(e);
      window.alert(`Could not save document settings:\n${msg}\n(UI state kept)`);
    } finally {
      setSavingDocFlags(false);
    }
  };

  // ---------- location ----------
  const updateJobLocation = async (jobIdValue, location_id) => {
    const body = { location_id: location_id ?? null };
    const attempts = [
      () => callJson("post", `jobs/${jobIdValue}/location`, body),
      () => callJson("post", `jobs/${jobIdValue}/update`, body),
      () => callJson("put", `jobs/${jobIdValue}`, body),
      () => callJson("patch", `jobs/${jobIdValue}`, body),
      () =>
        callJson("post", `job-locations`, {
          job_id: Number(jobIdValue),
          location_id: Number(location_id),
        }),
      () =>
        callJson("post", `jobs/${jobIdValue}/locations`, {
          location_id: Number(location_id),
        }),
    ];
    let lastErr = null;
    for (const step of attempts) {
      try {
        await step();
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    try {
      const verify = await callJson("get", `jobs/${jobIdValue}`);
      const persisted = Number(verify?.data?.location_id) || null;
      if (persisted !== Number(location_id)) throw lastErr || new Error("Location did not persist.");
    } catch (e) {
      throw lastErr || e;
    }
    return true;
  };

  const handlePickExistingLocation = async (loc) => {
    if (!jobId || !loc?.id) return;
    try {
      await updateJobLocation(jobId, Number(loc.id));
      onChange("location_id", String(loc.id));
      setLocationName(loc.name || `Location #${loc.id}`);

      try {
        const lr = await callJson("get", `locations/${loc.id}`);
        const l = lr?.data || {};
        setLocationCoords({ lat: l.lat ?? null, lon: l.lon ?? null });
      } catch {
        setLocationCoords({ lat: null, lon: null });
      }

      onSaved?.({ ok: true, message: "Location updated." });
      setLocPickerOpen(false);
    } catch (e) {
      const msg = extractHttpError(e);
      onSaved?.({ ok: false, message: msg || "Failed to set job location." });
    }
  };

  const openEditExistingLocation = async () => {
    if (!form.location_id || locationLoadingForEdit) return;
    setLocationEditError("");
    setLocationLoadingForEdit(true);
    try {
      const response = await callJson("get", `locations/${form.location_id}`);
      if (!response?.data || !sameId(response.data.id, form.location_id)) {
        throw new Error("Could not load the linked Location.");
      }
      setLocEditOpen(true);
    } catch (error) {
      setLocationEditError(extractHttpError(error));
    } finally {
      setLocationLoadingForEdit(false);
    }
  };

  const handleUpdatedLocation = (location) => {
    // Editing a Location updates the same record, so no Job association write is needed.
    if (!sameId(location?.id, form.location_id)) return;
    setLocationName(location.name || `Location #${location.id}`);
    setLocationCoords({ lat: location.lat ?? null, lon: location.lon ?? null });
    setLocEditOpen(false);
    onSaved?.({ ok: true, message: "Linked Location updated." });
  };

  const handleCreatedLocation = async (created) => {
    if (!created?.id || !jobId) return;
    try {
      await updateJobLocation(jobId, Number(created.id));
      onChange("location_id", String(created.id));
      setLocationName(created.name || `Location #${created.id}`);
      setLocationCoords({ lat: created.lat ?? null, lon: created.lon ?? null });

      onSaved?.({ ok: true, message: "Location created and linked to job." });
      setLocCreateOpen(false);
      setLocPickerOpen(false);
    } catch (e) {
      const msg = extractHttpError(e);
      onSaved?.({ ok: false, message: msg || "Failed to link new location to job." });
    }
  };

  // ---------- UI helpers ----------
  const resourceOptions = useMemo(() => {
    return Object.keys(resourceMap)
      .sort((a, b) => String(resourceMap[a]).localeCompare(String(resourceMap[b])))
      .map((rid) => {
        const status = availability[Number(rid)] || "unknown";
        const badge =
          !form.use_week_commencing && form.scheduled_start && form.scheduled_end
            ? status === "available"
              ? " (available)"
              : status === "busy"
              ? " (busy)"
              : ""
            : "";
        return { value: rid, label: `${resourceMap[rid]}${badge}` };
      });
  }, [resourceMap, availability, form.scheduled_start, form.scheduled_end, form.use_week_commencing]);

  const swapPrimaryAndCo = () => {
    setForm((f) => {
      const a = f.assigned_resource_id || "";
      const b = f.co_assigned_resource_id || "";
      return { ...f, assigned_resource_id: b, co_assigned_resource_id: a };
    });
  };

  const clearCo = () =>
    setForm((f) => ({
      ...f,
      co_assigned_resource_id: "",
    }));

  const openEmailModal = (ctx) => {
    setActiveMessageContext(ctx || null);
    setEmailModalOpen(true);
  };
  const openSmsModal = (ctx) => {
    setActiveMessageContext(ctx || null);
    setSmsModalOpen(true);
  };
  const openWhatsAppModal = (ctx) => {
    setActiveMessageContext(ctx || null);
    setWhatsAppModalOpen(true);
  };

  if (!open) return null;

  const portalTarget = document.body;

  const authorityName = authorityEntry?.council_or_local_authority || adminArea?.name || "—";
  const gss = authorityEntry?.gss_code || adminArea?.gss_code || "—";
  const areaType = adminArea?.area_type || "—";
  const country = adminArea?.country_code || "—";

  const leadTime = authorityEntry?.usual_lead_time || "—";
  const costs = authorityEntry?.costs_gbp || "—";
  const licenceTypes = [authorityEntry?.licence_type_1, authorityEntry?.licence_type_2, authorityEntry?.licence_type_3]
    .filter(Boolean)
    .join(", ") || "—";

  const applyUrl = authorityEntry?.apply_license_url || "";
  const licensingInfoUrl = authorityEntry?.licensing_info_url || "";
  const permitUrl = authorityEntry?.permit_scheme_url || "";

  const isActive = authorityEntry?.is_active === true;

  const addHistoricWorkPackages = async () => {
    if (!jobId || !workPackageDrafts.length) return;
    if (workPackageDrafts.some((row) => !row.binding)) { setWorkPackageError("Every Work Package must resolve to a published workflow binding before it can be added."); return; }
    setWorkPackageSaving(true); setWorkPackageError("");
    try {
      for (const draft of workPackageDrafts) {
        const wpRes = await axiosInstance.post(`/api/jobs/${jobId}/work-packages`, { title: draft.title, package_type: draft.package_type || "work_package", work_package_type_id: Number(draft.work_package_type_id), work_package_subtype_id: draft.work_package_subtype_id ? Number(draft.work_package_subtype_id) : null, work_package_subtype2_id: draft.work_package_subtype2_id ? Number(draft.work_package_subtype2_id) : null, location_id: draft.location_id ? Number(draft.location_id) : null, assigned_resource_id: draft.assigned_resource_id ? Number(draft.assigned_resource_id) : null, week_commencing: form.use_week_commencing ? form.week_commencing || null : null, planned_start: form.use_week_commencing ? null : form.scheduled_start || null, planned_end: form.use_week_commencing ? null : form.scheduled_end || null, is_required: true });
        await axiosInstance.post(`/api/jobs/${jobId}/work-packages/${wpRes.data.id}/workflow/instantiate`, { force_template_version_id: draft.binding.workflow_template_version_id || null });
      }
      const { data } = await axiosInstance.get(`/api/jobs/${jobId}/work-packages`);
      setExistingWorkPackages(Array.isArray(data) ? data : []); setWorkPackageDrafts([]); setContainsWorkPackages(true);
      onSaved?.({ ok: true, message: "Work Package(s) added and workflow instantiated." });
    } catch (e) { setWorkPackageError(extractHttpError(e) || "Failed to add Work Package."); }
    finally { setWorkPackageSaving(false); }
  };

  return (
    <>
      {receiptPreview && createPortal(
        <div className="fixed inset-0 z-[300000] flex items-center justify-center bg-black/75 p-3" role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeExpenseReceipt(); }}>
          <div role="dialog" aria-modal="true" aria-label={`Receipt for expense ${receiptPreview.expenseId}`}
            className="flex h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b p-3">
              <strong>Expense #{receiptPreview.expenseId} — Receipt</strong>
              <button type="button" onClick={closeExpenseReceipt}
                className="rounded bg-btnGeneralNormal px-4 py-2 text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick">Close</button>
            </div>
            {receiptPreview.mime === "application/pdf" ? (
              <iframe title={`Expense ${receiptPreview.expenseId} receipt`} src={receiptPreview.url} className="min-h-0 w-full flex-1 border-0" />
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-gray-100 p-2">
                <img src={receiptPreview.url} alt={`Expense ${receiptPreview.expenseId} receipt`} className="max-h-full max-w-full object-contain" />
              </div>
            )}
          </div>
        </div>, document.body
      )}
      {createPortal(
        <>
          <WorkPackageMapPlanner open={workPackageMapOpen} jobId={jobId} onClose={() => setWorkPackageMapOpen(false)} onChanged={() => axiosInstance.get(`/api/jobs/${jobId}/work-packages`).then(({ data }) => { setExistingWorkPackages(Array.isArray(data) ? data : []); setContainsWorkPackages(true); }).catch(() => {})} />
          <LocationEditModal
            open={locCreateOpen}
            onClose={() => setLocCreateOpen(false)}
            onCreated={handleCreatedLocation}
          />
          <LocationEditModal
            key={`edit-location-${form.location_id || "none"}-${locEditOpen}`}
            open={locEditOpen}
            locationId={form.location_id ? Number(form.location_id) : null}
            onClose={() => setLocEditOpen(false)}
            onUpdated={handleUpdatedLocation}
          />
          <QuoteCreateModal
            key={`linked-quote-${persistedQuoteId || "none"}-${showLinkedQuote}`}
            open={showLinkedQuote && !!persistedQuoteId}
            onClose={() => setShowLinkedQuote(false)}
            contextQuoteId={persistedQuoteId}
            customerMap={customerMap}
            onQuoteChanged={() => onSaved?.({ ok: true, message: "Linked Quote changed." })}
          />

          <EmailMessageModal
            open={emailModalOpen}
            onClose={() => setEmailModalOpen(false)}
            jobId={jobId}
            context={activeMessageContext}
          />
          <SmsMessageModal
            open={smsModalOpen}
            onClose={() => setSmsModalOpen(false)}
            jobId={jobId}
            context={activeMessageContext}
          />
          <WhatsAppMessageModal
            open={whatsAppModalOpen}
            onClose={() => setWhatsAppModalOpen(false)}
            jobId={jobId}
            context={activeMessageContext}
          />
        </>,
        portalTarget
      )}

      {createPortal(
        <ModalWrapper
          onClose={onClose}
          overlayClassName="!fixed !inset-0 z-[100000]"
          panelClassName="z-[100001] w-full md:w-auto"
        >
          <h3 className="text-lg font-semibold mb-3">Edit Job</h3>

          {loading ? (
            <div className="py-4 text-sm text-gray-600">Loading job...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Job Ref</label>
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <input
                      className="w-full border rounded px-3 py-2 text-sm bg-white"
                      value={form.job_ref}
                      onChange={(e) => {
                        setRefMsg("");
                        setRefErr("");
                        onChange("job_ref", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveJobRef();
                        }
                      }}
                      placeholder="e.g. 3055"
                    />
                    <button
                      type="button"
                      onClick={saveJobRef}
                      disabled={savingRef || refDup || refCheckLoading}
                      className={`${
                        savingRef || refDup || refCheckLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
                      } text-white text-sm font-semibold px-3 rounded shadow`}
                      title={refDup ? "Duplicate Job Ref" : "Save Job Ref"}
                    >
                      {savingRef ? "Saving…" : refCheckLoading ? "Checking…" : "Save"}
                    </button>
                  </div>
                  {refDup && (
                    <div className="text-red-700 text-xs mt-1">
                      ❌ Duplicate: {refEvidence.map((x) => `#${x.id}`).join(", ")} already use this ref.
                    </div>
                  )}
                  {refErr && <div className="text-red-700 text-xs mt-1">❌ {refErr}</div>}
                  {refMsg && <div className="text-emerald-700 text-xs mt-1">✅ {refMsg}</div>}
                  <div className="text-[11px] text-gray-500 mt-1">
                    Will POST <code>/api/jobs/{jobId}/ref</code>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Status</label>
                  <input
                    className="w-full border rounded px-3 py-2 text-sm bg-gray-50 text-gray-700"
                    value={form.status}
                    readOnly
                    aria-label="Job status (read only)"
                  />
                </div>

                <div className="md:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="text-sm font-semibold text-gray-800">Job Lifecycle</div>
                  <div className="mt-1 text-xs text-gray-600">
                    Current status: <strong>{form.status || "Unknown"}</strong> · Progress: <strong>{form.progress === "" ? "—" : `${form.progress}%`}</strong>
                  </div>
                  {String(form.status).toLowerCase() === "completed" ? (
                    <div className="mt-2 text-sm font-medium text-emerald-700">This Job is completed.</div>
                  ) : !workPackagesChecked ? (
                    <div className="mt-2 text-sm text-amber-800">{workPackagesLoadError ? `Cannot check Work Packages: ${workPackagesLoadError}` : "Checking Work Packages before enabling completion…"}</div>
                  ) : existingWorkPackages.length > 0 || containsWorkPackages || workPackageDrafts.length > 0 ? (
                    <div className="mt-2 text-sm text-gray-700">Work Packages are present or being prepared. Complete through the Work Package workflow, or clear unsaved Work Package drafts first.</div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <label className="block text-xs font-medium text-gray-700" htmlFor="job-completion-reason">Completion reason (required; audited)</label>
                      <textarea
                        id="job-completion-reason"
                        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                        rows={2}
                        maxLength={1000}
                        placeholder="e.g. Work delivered and final invoice issued"
                        value={completionReason}
                        onChange={(e) => { setCompletionReason(e.target.value); setCompletionError(""); }}
                        disabled={completionBusy || saving}
                      />
                      <button
                        type="button"
                        onClick={completeJobWithoutWorkPackages}
                        disabled={completionBusy || saving || loading || completionReason.trim().length < 8}
                        className="min-h-11 rounded bg-btnActionNormal px-4 py-2 text-sm font-semibold text-white hover:bg-btnActionHover active:bg-btnActionClick disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {completionBusy ? "Completing…" : "Mark Job Complete (100%)"}
                      </button>
                    </div>
                  )}
                  {completionError && <p role="alert" className="mt-2 text-xs text-red-700">{completionError}</p>}
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Customer</label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm bg-white"
                    value={form.customer_id === null || form.customer_id === undefined ? "" : String(form.customer_id)}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {mapToOptions(customerMap || {}, "Customer")
                      .ensureValueInOptions(form.customer_id)
                      .map((o) => (
                        <option key={`customer_id-${o.value}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                  </select>
                </div>
                {renderSelect("contact_id", "Contact", contactMap || {})}
                {renderSelect("processed_by", "Processed By (User)", userMap || {})}
                {renderSelect("job_type_id", "Job Type", jobTypeMap || {})}

                {String(jobTypeMap?.[form.job_type_id] || "").toLowerCase().startsWith("anpr") && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Model Type</label>
                      <input
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.model_type}
                        onChange={(e) => onChange("model_type", e.target.value)}
                        placeholder="e.g. anpr_fixed"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Current Step</label>
                      <input
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.current_step}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Progress (%)</label>
                      <input
                        type="number"
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.progress}
                        readOnly
                        min={0}
                        max={100}
                      />
                    </div>
                  </>
                )}

                {renderSelect("job_subtype_id", "Job Subtype", jobSubtypeMap || {})}
                {renderSelect("job_subtype2_id", "Job Subtype 2", jobSubtype2Map || {})}

                <div className="md:col-span-2 rounded-xl border border-gray-300 p-3">
                  <label className="flex items-start gap-3 min-h-11 cursor-pointer"><input type="checkbox" className="w-5 h-5 mt-0.5" checked={containsWorkPackages} disabled={existingWorkPackages.length > 0} onChange={(e) => { setContainsWorkPackages(e.target.checked); if (!e.target.checked) setWorkPackageDrafts([]); }} /><span><span className="font-semibold block">Job contains Work Packages</span><span className="text-xs text-gray-500">Enable this for historic Jobs that now need Work Packages. Existing packages are never removed by unticking.</span></span></label>
                  {containsWorkPackages && <button type="button" onClick={() => setWorkPackageMapOpen(true)} className="mt-2 min-h-11 px-4 rounded bg-btnActionNormal hover:bg-btnActionHover text-white font-semibold">Open Work Package Map</button>}
                  {existingWorkPackages.length > 0 && <div className="mt-2 text-sm"><strong>Existing:</strong> {existingWorkPackages.map((wp) => wp.title || wp.package_ref).join(", ")}</div>}
                  {containsWorkPackages && <div className="mt-3"><WorkPackageComposer jobTypeName={jobTypeMap?.[form.job_type_id] || ""} resources={Object.entries(resourceMap || {}).map(([id, name]) => ({ id: Number(id), name }))} value={workPackageDrafts} onChange={setWorkPackageDrafts} />{!!workPackageError && <div className="text-sm text-red-700 mt-2">{workPackageError}</div>}{!!workPackageDrafts.length && <button type="button" onClick={addHistoricWorkPackages} disabled={workPackageSaving} className="mt-3 min-h-11 px-4 rounded bg-btnActionNormal hover:bg-btnActionHover text-white font-semibold disabled:opacity-50">{workPackageSaving ? "Adding Work Packages…" : "Add Work Packages & Initiate Workflow"}</button>}</div>}
                </div>

                {/* Q-12: Commercial provenance */}
                {renderCommercialSelectors()}

                {/* Week commencing mode */}
                <div className="md:col-span-2 rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold">Planning Mode</div>
                      <div className="text-xs text-gray-600">
                        Use week commencing for flexible planning without exact start/end slots.
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={!!form.use_week_commencing}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            use_week_commencing: e.target.checked,
                            scheduled_start: e.target.checked ? "" : f.scheduled_start,
                            scheduled_end: e.target.checked ? "" : f.scheduled_end,
                          }))
                        }
                      />
                      Week Commencing Mode
                    </label>
                  </div>

                  {form.use_week_commencing && (
                    <div className="mt-3">
                      <label className="block text-xs text-gray-600 mb-1">Week Commencing</label>
                      <input
                        type="date"
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.week_commencing || ""}
                        onChange={(e) => onChange("week_commencing", e.target.value)}
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Select the Monday for the job week. Historic and future weeks are supported. Exact assignment fields are ignored while this mode is enabled.
                      </p>
                    </div>
                  )}
                </div>

                {!form.use_week_commencing && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Assigned Resource{" "}
                        <span className="text-gray-400">
                          {form.scheduled_start && form.scheduled_end
                            ? "(availability shown)"
                            : "(set schedule to see availability)"}
                        </span>
                      </label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.assigned_resource_id ?? ""}
                        onChange={(e) => onChange("assigned_resource_id", e.target.value)}
                      >
                        <option value="">Select…</option>
                        {resourceOptions.map((o) => (
                          <option key={`res-${o.value}`} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <label className="block text-xs text-gray-600 mb-1">Co-Resource (optional)</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={swapPrimaryAndCo}
                            className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-[11px] font-semibold px-2 py-1 rounded shadow"
                            title="Swap primary and co"
                          >
                            Swap
                          </button>
                          <button
                            type="button"
                            onClick={clearCo}
                            className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-[11px] font-semibold px-2 py-1 rounded shadow"
                            title="Clear co-resource"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      <select
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.co_assigned_resource_id ?? ""}
                        onChange={(e) => onChange("co_assigned_resource_id", e.target.value)}
                      >
                        <option value="">None</option>
                        {resourceOptions.map((o) => (
                          <option key={`cores-${o.value}`} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>

                      <p className="text-[11px] text-gray-500 mt-1">
                        On save, a second assignment will be created for the co-resource using the same start/end. (No automatic removal of existing co-assignments.)
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Task ID</label>
                      <input
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.task_id}
                        onChange={(e) => onChange("task_id", e.target.value)}
                        placeholder="Optional external task ref"
                      />
                    </div>

                    <DateTime15Picker
                      label="Scheduled Start"
                      value={form.scheduled_start}
                      onChange={(v) => onChange("scheduled_start", v)}
                    />
                    <DateTime15Picker
                      label="Scheduled End"
                      value={form.scheduled_end}
                      onChange={(v) => onChange("scheduled_end", v)}
                    />
                  </>
                )}
              </div>

              <div className="mt-6 rounded-xl border border-gray-200 bg-white">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">Location</div>
                    <div className="text-xs text-gray-600">
                      {form.location_id
                        ? `${form.location_id}${locationName ? ` · ${locationName}` : ""}`
                        : "No location set"}
                      {locationCoords?.lat != null && locationCoords?.lon != null && (
                        <div className="text-xs text-gray-500">
                          Lat/Lon: {Number(locationCoords.lat).toFixed(6)}, {Number(locationCoords.lon).toFixed(6)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setLocPickerOpen((s) => !s)}
                      className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
                    >
                      {locPickerOpen ? "Hide Picker" : "Set Location"}
                    </button>
                    <button
                      type="button"
                      onClick={openEditExistingLocation}
                      disabled={!form.location_id || locationLoadingForEdit}
                      className={`text-white text-sm font-semibold py-2 px-3 rounded shadow ${!form.location_id || locationLoadingForEdit ? "bg-gray-400 cursor-not-allowed" : "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"}`}
                    >
                      {locationLoadingForEdit ? "Loading…" : "Edit Location"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocCreateOpen(true)}
                      className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
                    >
                      Create Location
                    </button>
                  </div>
                </div>

                {locationEditError && <div className="px-4 pb-2 text-xs text-red-700">{locationEditError}</div>}
                {locPickerOpen && (
                  <div className="px-4 pb-4">
                    <InlineLocationPicker
                      currentId={form.location_id}
                      currentName={locationName}
                      onChoose={handlePickExistingLocation}
                      onCreate={() => setLocCreateOpen(true)}
                    />
                  </div>
                )}
              </div>

              <ExpectedWeatherCard
                title="Expected Weather"
                lat={locationCoords?.lat}
                lon={locationCoords?.lon}
                locationLabel={locationName || ""}
                jobStart={!form.use_week_commencing ? form.scheduled_start : ""}
                jobEnd={!form.use_week_commencing ? form.scheduled_end : ""}
              />

              <TrafficIntelligenceCard
                title="Traffic Intelligence"
                lat={locationCoords?.lat}
                lon={locationCoords?.lon}
                locationLabel={locationName || ""}
                jobTypeLabel={jobTypeMap?.[form.job_type_id] || ""}
                persist={true}
                compact={false}
              />

              <WorksIntelligenceCard
                title="Works Intelligence"
                jobId={jobId}
                lat={locationCoords?.lat}
                lon={locationCoords?.lon}
                locationLabel={locationName || ""}
                radiusM={5000}
                limit={20}
                windowStart={!form.use_week_commencing && form.scheduled_start ? isoNoMs(form.scheduled_start) : ""}
                windowEnd={!form.use_week_commencing && form.scheduled_end ? isoNoMs(form.scheduled_end) : ""}
                persist={true}
                showRefresh={true}
                autoFetch={true}
                showTechnicalFooter={true}
              />

              <FloodIntelligenceCard
                title="Flood Intelligence"
                lat={locationCoords?.lat}
                lon={locationCoords?.lon}
                locationLabel={locationName || ""}
                jobTypeLabel={jobTypeMap?.[form.job_type_id] || ""}
                persist={true}
                compact={false}
              />

              <div className="mt-4 rounded-xl border border-gray-200 bg-white">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--accent-color)" }}>
                      Authority (Auto-Resolved)
                    </div>
                    <div className="text-xs text-gray-600">
                      Derived from location coordinate and matched to Authority Directory.
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {authorityLoading ? (
                      <span className="text-xs text-gray-500">Resolving…</span>
                    ) : authorityEntry ? (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
                        }`}
                        title={isActive ? "Directory entry is active" : "Directory entry is inactive"}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full ${isActive ? "bg-emerald-600" : "bg-gray-400"}`} />
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800">
                        No directory entry
                      </span>
                    )}
                  </div>
                </div>

                <div className="px-4 pb-4">
                  {authorityErr && <div className="text-xs text-red-600 mb-2">❌ {authorityErr}</div>}

                  {!form.location_id ? (
                    <div className="text-xs text-gray-600 italic">Set a location to enable authority auto-resolution.</div>
                  ) : locationCoords?.lat == null || locationCoords?.lon == null ? (
                    <div className="text-xs text-gray-600 italic">Location has no coordinates; cannot resolve authority.</div>
                  ) : (
                    <div className="rounded-lg border border-gray-100 bg-white p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <div>
                          <div className="text-xs text-gray-500">Authority Name</div>
                          <div className="text-base font-semibold text-gray-900">{authorityName}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">GSS Code</div>
                          <div className="text-base font-semibold text-gray-900">{gss}</div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">{adminArea?.has_separate_upper_tier ? "Upper-tier County" : "County / Unitary Authority"}</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {adminArea?.ctyua_name || "—"}
                            {adminArea?.ctyua_gss_code ? ` (${adminArea.ctyua_gss_code})` : ""}
                          </div>
                          {adminArea?.ctyua_gss_code && !adminArea?.has_separate_upper_tier && (
                            <div className="text-xs text-gray-500">Single-tier / self-mapped authority</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Area Type</div>
                          <div className="text-sm font-semibold text-gray-900">{areaType}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Country</div>
                          <div className="text-sm font-semibold text-gray-900">{country}</div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Usual Lead Time</div>
                          <div className="text-sm font-semibold text-gray-900">{leadTime}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Costs</div>
                          <div className="text-sm font-semibold text-gray-900">{costs}</div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Licence Types (1–3)</div>
                          <div className="text-sm font-semibold text-gray-900">{licenceTypes}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Status</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {authorityEntry ? (isActive ? "Active" : "Inactive") : "—"}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Licensing URL</div>
                          {applyUrl ? (
                            <a
                              href={applyUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm font-semibold underline"
                              style={{ color: "var(--accent-color)" }}
                            >
                              Open licensing
                            </a>
                          ) : (
                            <div className="text-sm text-gray-600">—</div>
                          )}
                          {licensingInfoUrl ? (
                            <div className="mt-1">
                              <a
                                href={licensingInfoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-semibold underline"
                                style={{ color: "var(--accent-color)" }}
                              >
                                Licensing info
                              </a>
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Permit Scheme URL</div>
                          {permitUrl ? (
                            <a
                              href={permitUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm font-semibold underline"
                              style={{ color: "var(--accent-color)" }}
                            >
                              Open permit scheme
                            </a>
                          ) : (
                            <div className="text-sm text-gray-600">—</div>
                          )}
                        </div>
                      </div>

                      {!authorityEntry && adminArea?.gss_code && (
                        <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-3">
                          No authority_directory entry configured for GSS {adminArea.gss_code}. Configure it in Authority Directory.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  value={form.notes}
                  onChange={(e) => onChange("notes", e.target.value)}
                  rows={4}
                />
              </div>

              <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold bg-btnGeneralNormal text-white"
                  onClick={() => setTabDocsOpen((s) => !s)}
                  aria-expanded={tabDocsOpen}
                  aria-controls="job-docs-panel"
                >
                  <span>Documents</span>
                  <span className="ml-2">{tabDocsOpen ? "▾" : "▸"}</span>
                </button>

                {tabDocsOpen && (
                  <div
                    id="job-docs-panel"
                    className="border-t border-gray-200 p-4 space-y-4 bg-white"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-sm mb-1 font-medium">Upload files</label>
                        <input
                          type="file"
                          accept={acceptTypes}
                          multiple
                          onChange={handleFilesPicked}
                          disabled={uploading}
                        />
                        {uploading && <div className="text-xs text-gray-600 mt-1">Uploading…</div>}
                        {uploadErr && <div className="text-xs text-red-600 mt-1">{String(uploadErr)}</div>}
                        <p className="text-xs text-gray-500 mt-1">
                          Supported: Microsoft docs (Word, Excel, PowerPoint), PDF, images, video.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={docPortalVisible}
                            onChange={(e) => setDocPortalVisible(e.target.checked)}
                          />
                          Visible in customer portal (default for new uploads)
                        </label>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Tags (CSV)</label>
                          <input
                            className="w-full border rounded px-3 py-2 text-sm"
                            placeholder="e.g. proposal, signed"
                            value={docTags}
                            onChange={(e) => setDocTags(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-2 bg-white border-b border-gray-200 font-semibold text-sm">
                        Recent files for this job
                      </div>
                      <div className="overflow-auto">
                        <table className="w-full text-sm text-left text-gray-700">
                          <thead>
                            <tr className="bg-white">
                              <th className="px-3 py-2 border-b">Name</th>
                              <th className="px-3 py-2 border-b">Version</th>
                              <th className="px-3 py-2 border-b">Size</th>
                              <th className="px-3 py-2 border-b">Uploaded</th>
                              <th className="px-3 py-2 border-b">Portal</th>
                              <th className="px-3 py-2 border-b">Downloadable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingDocs ? (
                              <tr>
                                <td colSpan={6} className="px-3 py-3 text-gray-500 italic">
                                  Loading documents…
                                </td>
                              </tr>
                            ) : docs.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="px-3 py-3 text-gray-500 italic">
                                  No documents yet. Use the uploader above.
                                </td>
                              </tr>
                            ) : (
                              docs.map((d) => (
                                <tr key={d.id} className="odd:bg-gray-50">
                                  <td className="px-3 py-2">
                                    {d.latest_url ? (
                                      <a
                                        href={d.latest_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 hover:underline"
                                        title={d.latest_url}
                                      >
                                        {d.name}
                                      </a>
                                    ) : (
                                      d.name
                                    )}
                                  </td>
                                  <td className="px-3 py-2">v{d.latest_version || 1}</td>
                                  <td className="px-3 py-2">{formatBytes(d.size_bytes)}</td>
                                  <td className="px-3 py-2">{formatDateGB(d.uploaded_at)}</td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={!!d.portal_visible}
                                      onChange={() => toggleDocPortal(d.id)}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={!!d.downloadable}
                                      onChange={() => toggleDocDownloadable(d.id)}
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="px-4 py-2 bg-white border-t flex justify-end">
                        <button
                          onClick={saveDocFlags}
                          disabled={savingDocFlags}
                          className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow"
                        >
                          {savingDocFlags ? "Saving…" : "Save Document Settings"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <JobMessagesAccordian
                  jobId={jobId}
                  defaultOpen={false}
                  onNewEmail={openEmailModal}
                  onNewSms={openSmsModal}
                  onNewWhatsApp={openWhatsAppModal}
                />
              </div>

              <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold bg-btnGeneralNormal text-white"
                  onClick={() => setTabExpensesOpen((s) => !s)}
                  aria-expanded={tabExpensesOpen}
                  aria-controls="job-expenses-panel"
                >
                  <span>Expenses</span>
                  <span className="ml-2">{tabExpensesOpen ? "▾" : "▸"}</span>
                </button>

                {tabExpensesOpen && (
                  <div
                    id="job-expenses-panel"
                    className="border-t border-gray-200 p-4 space-y-4 bg-white"
                  >
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("vision:open-expense-create", {
                          detail: { customerId: form.customer_id, jobId },
                        }))}
                        disabled={!jobId || !form.customer_id}
                        className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow"
                      >
                        Add Expense
                      </button>
                    </div>

                    {expenseErr && <div className="text-xs text-red-600">{expenseErr}</div>}

                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-2 bg-white border-b border-gray-200 font-semibold text-sm">
                        Job Expenses
                      </div>
                      <div className="overflow-auto">
                        <table className="w-full text-sm text-left text-gray-700">
                          <thead>
                            <tr className="bg-white">
                              <th className="px-3 py-2 border-b">Date</th>
                              <th className="px-3 py-2 border-b">Category</th>
                              <th className="px-3 py-2 border-b">Amount</th>
                              <th className="px-3 py-2 border-b">Note</th>
                              <th className="px-3 py-2 border-b text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingExpenses ? (
                              <tr>
                                <td colSpan={5} className="px-3 py-3 text-gray-500 italic">
                                  Loading expenses…
                                </td>
                              </tr>
                            ) : expenses.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-3 py-3 text-gray-500 italic">
                                  No expenses captured for this job.
                                </td>
                              </tr>
                            ) : (
                              expenses.map((x) => (
                                <tr key={x.id} className="odd:bg-gray-50">
                                  <td className="px-3 py-2">
                                    {(x.occurred_at || x.date) ? new Date(x.occurred_at || x.date).toLocaleDateString("en-GB") : ""}
                                  </td>
                                  <td className="px-3 py-2">{x.category || ""}</td>
                                  <td className="px-3 py-2">
                                    {typeof x.amount === "number" ? x.amount.toFixed(2) : x.amount || ""}
                                  </td>
                                  <td className="px-3 py-2">{x.description || x.note || ""}</td>
                                  <td className="px-3 py-2 text-center">
                                    {x.receipt_url && (
                                      <button
                                        type="button"
                                        onClick={() => viewExpenseReceipt(x)}
                                        disabled={receiptBusyId !== null}
                                        className="mr-2 bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded shadow"
                                      >
                                        {receiptBusyId === x.id ? "Opening…" : "View"}
                                      </button>
                                    )}
                                    <button
                                      onClick={async () => {
                                        try {
                                          await callJson("delete", `expenses/${x.id}`);
                                          setExpenses((rows) => rows.filter((r) => r.id !== x.id));
                                        } catch (e) {
                                          window.alert(extractHttpError(e));
                                        }
                                      }}
                                      className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Stage 4C: Independent manual Job Cost Ledger; Expenses above remains unchanged. */}
              <JobCostLedgerAccordion jobId={jobId} open={open} workPackages={existingWorkPackages} />

              <div className="mt-4 flex justify-between">
                <button
                  onClick={onClose}
                  className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
                >
                  Close
                </button>
                <button
                  onClick={saveJob}
                  disabled={saving}
                  className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-60 text-white text-sm font-semibold py-2 px-3 rounded shadow"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </>
          )}
        </ModalWrapper>,
        portalTarget
      )}
    </>
  );
};

export default JobEditModal;

/* ---------- InlineLocationPicker (lightweight) ---------- */
const InlineLocationPicker = ({ currentId, currentName, onChoose, onCreate }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const search = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axiosInstance.get("locations", {
        params: q ? { q, limit: 25 } : { limit: 25 },
      });
      const arr = Array.isArray(r.data) ? r.data : [];
      setRows(arr);
    } catch (e) {
      setErr(extractHttpError(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="px-4 py-3 flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-gray-600 mb-1">Search Locations</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm bg-white"
            placeholder="Name or postcode"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          onClick={search}
          className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Search
        </button>
        <button
          onClick={onCreate}
          className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionHover text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Create Location
        </button>
      </div>

      <div className="px-4 pb-3">
        <div className="text-xs text-gray-600 mb-2">
          Current: {currentId ? `${currentId}${currentName ? ` · ${currentName}` : ""}` : "—"}
        </div>
        {err && <div className="text-xs text-red-600 mb-2">❌ {err}</div>}
        <div className="max-h-56 overflow-auto border rounded">
          <table className="w-full text-sm text-left text-gray-700">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 border-b">ID</th>
                <th className="px-3 py-2 border-b">Name</th>
                <th className="px-3 py-2 border-b">Postcode</th>
                <th className="px-3 py-2 border-b text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-gray-500 italic">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-gray-500 italic">
                    No results.
                  </td>
                </tr>
              ) : (
                rows.map((l) => (
                  <tr key={l.id} className="odd:bg-gray-50">
                    <td className="px-3 py-2">{l.id}</td>
                    <td className="px-3 py-2">{l.name || ""}</td>
                    <td className="px-3 py-2">{l.postcode || ""}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onChoose(l)}
                        className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-1 rounded shadow"
                      >
                        Use
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};